import {
  addDays as addDaysFns,
  addHours as addHoursFns,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isValid as isDateValid,
  isToday,
  parse,
  roundToNearestMinutes,
  startOfDay,
  subDays as subDaysFns,
  subHours as subHoursFns,
  subMinutes as subMinutesFns,
} from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

export interface IDateService {
  currentDay: Date
  timeZone: string
  currentDayByTimeZone: (timeZone: string) => Date
  toDate: (date: string) => Date
  addHours: (date: Date, hours: number) => Date
  addDays: (date: Date, days: number) => Date
  subtractMinutes(date: Date, minutes: number): Date
  subtractDays: (date: Date, days: number) => Date
  subtractHours: (date: Date, hours: number) => Date
  difference: (date1: Date, date2: Date, unit?: 'S' | 'M' | 'H' | 'D') => number
  dateToYyMmDd: (date: Date) => string
  format: (date: Date, format: string) => string
  timeToHI: (date: Date) => string
  timeToDate: (time: string) => Date
  timeToHM: (time: Date | string) => string
  isToday: (date: Date) => boolean
  roundMinutes: (date: Date) => Date
  getCurrentDateByTimeZone: (timeZone: string) => Date
  toZonedDate: (date: Date | string, timeZone: string) => Date
  toZonedDateTime: (date: string, time: string, timeZone: string) => Date
  compare: (date1: Date, date2: Date) => number
}

export class DateService implements IDateService {
  /**
   * Returns the current day with time set to midnight (00:00:00).
   */
  get currentDay() {
    return startOfDay(new Date())
  }

  /**
   * Returns the current day with time set to midnight (00:00:00) at specified timezone.
   */

  currentDayByTimeZone(timeZone: string) {
    return toZonedTime(startOfDay(new Date()), timeZone)
  }

  /**
   * Returns the system's current time zone string.
   */
  get timeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  /**
   * Checks whether the input is a valid Date object.
   */
  private isValidDate(input: unknown): input is Date {
    return input instanceof Date && isDateValid(input)
  }

  /**
   * Parses a date string (`yyyy-MM-dd`) into a Date object.
   * Returns current date if invalid.
   */
  toDate(date: string): Date {
    if (typeof date !== 'string') return new Date()
    const parsedDate = parse(date, 'yyyy-MM-dd', new Date())
    return this.isValidDate(parsedDate) ? parsedDate : new Date(date)
  }

  /**
   * Adds a number of days to the given date.
   */
  addDays(date: Date, days: number): Date {
    if (!this.isValidDate(date) || typeof days !== 'number') return new Date()
    return addDaysFns(date, days)
  }

  /**
   * Adds a number of hours to the given date.
   */
  addHours(date: Date, hours: number): Date {
    if (!this.isValidDate(date) || typeof hours !== 'number') return new Date()
    return addHoursFns(date, hours)
  }

  /**
   * Subtracts minutes from a given date.
   */
  subtractMinutes(date: Date, minutes: number): Date {
    if (!this.isValidDate(date) || typeof minutes !== 'number')
      return new Date()
    return subMinutesFns(date, minutes)
  }

  /**
   * Subtracts days from a given date.
   */
  subtractDays(date: Date, days: number): Date {
    if (!this.isValidDate(date) || typeof days !== 'number') return new Date()
    return subDaysFns(date, days)
  }

  /**
   * Subtracts hours from a given date.
   */
  subtractHours(date: Date, hours: number): Date {
    if (!this.isValidDate(date) || typeof hours !== 'number') return new Date()
    return subHoursFns(date, hours)
  }

  /**
   * Returns the difference between two dates in the specified unit.
   * - `S` = seconds
   * - `M` = minutes
   * - `H` = hours
   * - `D` = days
   */
  difference(
    date1: Date,
    date2: Date,
    unit: 'S' | 'M' | 'H' | 'D' = 'M',
  ): number {
    if (!this.isValidDate(date1) || !this.isValidDate(date2)) return 0

    switch (unit) {
      case 'S':
        return differenceInSeconds(date1, date2)
      case 'M':
        return differenceInMinutes(date1, date2)
      case 'H':
        return differenceInHours(date1, date2)
      case 'D':
        return differenceInDays(date1, date2)
      default:
        return differenceInMinutes(date1, date2)
    }
  }

  /**
   * Formats a Date object as `yyyy-MM-dd`.
   */
  dateToYyMmDd(date: Date): string {
    return this.isValidDate(date) ? format(date, 'yyyy-MM-dd') : ''
  }

  format(date: Date, formatStr: string): string {
    return this.isValidDate(date) ? format(date, formatStr) : ''
  }

  /**
   * Formats a Date object to `HH:mm` (hours:minutes).
   */
  timeToHI(date: Date): string {
    return this.isValidDate(date) ? format(date, 'HH:mm') : ''
  }

  /**
   * Parses a time string (`HH:mm:ss`) into a Date object.
   * Returns current date if invalid.
   */
  timeToDate(time: string): Date {
    if (typeof time !== 'string') return new Date()

    const hasSeconds = time.split(':').length === 3
    const format = hasSeconds ? 'HH:mm:ss' : 'HH:mm'

    const today = new Date()
    const parsedDate = parse(time, format, today)

    return this.isValidDate(parsedDate)
      ? parsedDate
      : new Date(today.setHours(0, 0, 0, 0))
  }

  /**
   * Converts a Date or time string into `HH:mm` format.
   */
  timeToHM(time: Date | string): string {
    if (typeof time === 'string') {
      const parsed = this.timeToDate(time)
      return this.isValidDate(parsed) ? format(parsed, 'HH:mm') : ''
    }
    return this.isValidDate(time) ? format(time, 'HH:mm') : ''
  }

  /**
   * Checks if the given date is today.
   */
  isToday(input: Date | string): boolean {
    const date = input instanceof Date ? input : this.toDate(input)
    return this.isValidDate(date) && isToday(date)
  }

  /**
   * Rounds a date to the nearest 5 minutes (ceiling).
   */
  roundMinutes(date: Date): Date {
    if (!this.isValidDate(date)) return new Date()
    try {
      return roundToNearestMinutes(date, {
        nearestTo: 5,
        roundingMethod: 'ceil',
      })
    } catch {
      return new Date()
    }
  }

  /**
   * Returns the current date/time in the specified time zone.
   * Falls back to system time zone if invalid.
   */
  getCurrentDateByTimeZone(timeZone: string): Date {
    try {
      const zone =
        typeof timeZone === 'string' && timeZone ? timeZone : this.timeZone
      return toZonedTime(new Date(), zone, { timeZone: zone })
    } catch {
      return new Date()
    }
  }

  /**
   * Converts a date (string or Date) to the specified time zone.
   */
  toZonedDate(date: Date | string, timeZone: string): Date {
    if (typeof date === 'string') {
      return toZonedTime(this.toDate(date), timeZone)
    }
    return toZonedTime(date, timeZone)
  }

  /**
   * Parses a date + time string into the correct time zone.
   * Example: toZonedDateTime("2025-09-08", "16:45", "Europe/Berlin")
   */
  toZonedDateTime(date: string, time: string, timeZone: string): Date {
    const timeStr = time.split(':').length === 2 ? `${time}:00` : time

    const dateTimeStr = `${date}T${timeStr}`
    const parsed = parse(dateTimeStr, "yyyy-MM-dd'T'HH:mm:ss", new Date())
    return toZonedTime(parsed, timeZone, { timeZone })
  }

  /**
   * Compares two dates.
   * @returns -1 if `date1 < date2`, 1 if `date1 > date2`, 0 if equal.
   */
  compare(date1: Date, date2: Date): number {
    if (!this.isValidDate(date1) || !this.isValidDate(date2)) return 0

    const time1 = date1.getTime()
    const time2 = date2.getTime()

    if (time1 < time2) return -1
    if (time1 > time2) return 1
    return 0
  }
}
